import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Droplets, LayoutGrid, Users, Truck, Package, LogOut, TrendingUp, Clock, CheckCircle2, ExternalLink, Settings, Repeat,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatJMD } from "@/lib/format";
import { AdminLogin } from "@/pages/AdminLogin";
import { OrdersTab } from "@/pages/admin/OrdersTab";
import { CustomersTab } from "@/pages/admin/CustomersTab";
import { LogisticsTab } from "@/pages/admin/LogisticsTab";
import { ProductsTab } from "@/pages/admin/ProductsTab";
import { StandingOrdersTab } from "@/pages/admin/StandingOrdersTab";
import { SettingsTab } from "@/pages/admin/SettingsTab";
import { Button } from "@/components/ui/button";

const TABS = [
  { k: "orders", label: "Orders", icon: LayoutGrid, testid: "admin-tab-orders-list" },
  { k: "customers", label: "Customers", icon: Users, testid: "admin-tab-customers-crm" },
  { k: "logistics", label: "Logistics", icon: Truck, testid: "admin-tab-delivery-logistics" },
  { k: "standing", label: "Standing Orders", icon: Repeat, testid: "admin-tab-standing-orders" },
  { k: "products", label: "Products & Pricing", icon: Package, testid: "admin-tab-product-pricing" },
  { k: "settings", label: "Settings", icon: Settings, testid: "admin-tab-settings" },
];

export default function AdminPortal() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [products, setProducts] = useState([]);

  const reload = useCallback(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/deliveries").then((r) => setDeliveries(r.data)).catch(() => {});
    api.get("/products", { params: { include_inactive: true } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="min-h-screen cw-ocean-gradient flex items-center justify-center">
        <Droplets className="w-10 h-10 text-sky-300 animate-pulse" />
      </div>
    );
  }

  if (!user) return <AdminLogin />;

  const revenue = orders.filter((o) => o.status !== "cancelled").reduce((a, o) => a + (o.total_amount || 0), 0);
  const pending = orders.filter((o) => o.status === "pending").length;
  const delivered = orders.filter((o) => o.status === "delivered").length;

  const stats = [
    { label: "Total Revenue", value: formatJMD(revenue), icon: TrendingUp, color: "text-emerald-600 bg-emerald-50" },
    { label: "Total Orders", value: orders.length, icon: Package, color: "text-sky-600 bg-sky-50" },
    { label: "Pending", value: pending, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Delivered", value: delivered, icon: CheckCircle2, color: "text-indigo-600 bg-indigo-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <header className="cw-ocean-gradient text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="Cool Waves" className="w-10 h-10 rounded-xl object-cover" />
            <div className="leading-none">
              <div className="font-display font-extrabold">Cool Waves Admin</div>
              <div className="text-[11px] text-sky-200">CRM & Logistics</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-sky-100 hover:text-white hover:bg-white/10" data-testid="admin-view-site">
              <ExternalLink className="w-4 h-4 mr-1.5" /> View Site
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-sky-100 hover:text-white hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-display text-2xl font-extrabold text-slate-900 mt-3">{s.value}</div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={t.testid}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                tab === t.k ? "bg-sky-600 text-white shadow-lg shadow-sky-600/25" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersTab orders={orders} products={products} reload={reload} />}
        {tab === "customers" && <CustomersTab customers={customers} />}
        {tab === "logistics" && <LogisticsTab orders={orders} deliveries={deliveries} reload={reload} />}
        {tab === "products" && <ProductsTab products={products} reload={reload} />}
        {tab === "standing" && <StandingOrdersTab reload={reload} />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
